import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"
import { useRef, useEffect } from "react"
import * as y from "yup"
import { isNaN } from "lodash"
import { format } from "date-fns"
import { TZDate } from "@date-fns/tz"
import { isArray, isObject, isPlainObject, map, mapValues } from "lodash"

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": ["text-tiny"],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

function formatUuid(bytes: Uint8Array): string {
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"))

  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-")
}

export function createUuid(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID()
  }

  const bytes = new Uint8Array(16)

  if (typeof globalThis.crypto?.getRandomValues === "function") {
    globalThis.crypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  return formatUuid(bytes)
}

/**
 * Get an enum type from object type
 */
export type EnumsOf<T> = T[keyof T]

/**
 * Remove all the unnecessary intersections,
 * inheritance and other garbage to see resulting
 * fields clearly
 */
export type Prettify<T> = {
  [K in keyof T]: T[K]
} & {}

export const safeNumber = y.mixed().transform((val) => {
  if (!val) {
    return val
  }

  const tryNumber = Number(val)
  if (isNaN(tryNumber)) {
    return val
  }

  return tryNumber
})

type AnyFn = (...args: any[]) => void
export type UseDebounceByHashReturn = (hash: string, fn: AnyFn, delayOverride?: number) => void

export function useDebounceByHash(
  delay: number = 300
): UseDebounceByHashReturn {
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  )

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout)
      timersRef.current.clear()
    }
  }, [])

  function debounce(hash: string, fn: AnyFn, delayOverride?: number) {
    const timers = timersRef.current

    if (timers.has(hash)) {
      clearTimeout(timers.get(hash)!)
    }

    const timeout = setTimeout(() => {
      timers.delete(hash)
      fn()
    }, delayOverride || delay)

    timers.set(hash, timeout)
  }

  return debounce
}

/**
 * Disable back button
 */
export function useDisableBack(enabled: boolean = true) {
  useEffect(() => {
    history.pushState(null, document.title, location.href)
    const handler = function () {
      if (enabled) {
        history.pushState(null, document.title, location.href)
      }
    }
    window.addEventListener("popstate", handler)

    return () => {
      window.removeEventListener("popstate", handler)
    }
  }, [enabled])
}

const validNumberSymbols = ["-", "+", "e"]
export function isValidNumber(str: string): boolean {
  if (validNumberSymbols.includes(str)) {
    return true
  }

  return !isNaN(Number(str))
}

export function formatShiftTime(str?: string) {
  const placeholder = "---"

  try {
    if (!str) {
      return placeholder
    }

    return format(new TZDate(str, "UTC"), "HH:mm")
  } catch {
    return placeholder
  }
}

export class UnixTimestamp extends TZDate {
  constructor() {
    super("1970-01-01T00:00:00Z", "UTC")
  }
}

export function formatDate(date: Date): string {
  try {
    return format(date, "dd/MM/yyyy")
  } catch {
    return ""
  }
}

export const mapValuesDeep = (obj: any, fn: any, key?: any): any =>
  isArray(obj)
    ? map(obj, (innerObj, idx) => mapValuesDeep(innerObj, fn, idx))
    : isPlainObject(obj)
      ? mapValues(obj, (val, key) => mapValuesDeep(val, fn, key))
      : isObject(obj)
        ? obj
        : fn(obj, key)
